import React, { FC, useCallback, useEffect, useState } from 'react';
import { Button, Form, Input, Modal, Select, Space, TreeSelect } from 'antd';
import { FormWrapper } from 'common/components/form-wrapper/form-wrapper';
import { IModal } from 'common/models/model.interface';
import { ReactComponent as SaveOutlined } from 'assets/icons/save.svg';
import { GuestValidity } from 'common/enums/guest-validity.enum';
import { DATE_MMMM_D_YYYY, DATE_YYYY_MM_DD } from 'common/constans/date.constants';
import { ICreateGuest, ICreateGuestLimitations, IUpdateGuest } from 'common/services/guest-service/guest.service.types';
import guestService from 'common/services/guest-service/guest.service';
import { IAddEditGuestModal } from '../guest-list.types';
import './add-edit-guest-modal.scss';
import { guestTypeOptions, serviceGuestTypeOptions } from 'common/constans/guest-type-options.constant';
import { keyPermissionOptions } from 'features/eiq-manage/constants/guest-key-permission-options.constant';
import { daysOfWeekOptions } from 'features/eiq-manage/constants/guest-days-of-week.options.constant';
import { addEditGuestValidation } from '../../../../../../../../common/validation/guest.validation';
import { daysOfWeek } from 'common/constans/common.constants';
import { GuestType } from 'common/enums/guest-type.enum';
import { notification } from '../../../../../../../../common/utils/notification';
import { getVisitDateByGuestValidity, mapToServerGuestValidity } from 'common/helpers/guest-validity.helper';
import SelectState from 'common/components/form-elements/select-state/select-state';
import residentStore from 'features/eiq-manage/stores/resident.store';
import { ResidentTypesEnum } from 'common/enums/resident-types.enum';
import { isServiceGuest } from 'common/helpers/guest-type.helpers';
import configurationStore from 'common/stores/configuration/configuration.store';
import InputPhoneFormItem from 'common/components/form-elements/input-phone/form-item/input-phone-form-item';
import { checkDateInRange, TimeDirection } from 'common/helpers/date.helper';
import dayjs from 'dayjs';
import residentsService from 'common/services/residents-service/residents.service';
import { IGuestValidityOption } from 'common/constans/guest-validity-options.constant';
import EiqDatePicker from 'common/components/date-picker/eiq-date-picker';

const AddEditGuestModal: FC<IModal<IAddEditGuestModal, any>> = ({ isOpen, onClose, title, initData, isEdit }) => {
  const [form] = Form.useForm();
  const { SHOW_PARENT } = TreeSelect;
  const { TextArea } = Input;

  const [isShowDateRange, setIsShowDateRange] = useState<boolean>(false);
  const [isShowDaysOfVisits, setIsShowDaysOfVisits] = useState<boolean>(false);
  const [isShowCompanyName, setIsShowCompanyName] = useState<boolean>(false);
  const [isShowVisitDate, setIsShowVisitDate] = useState<boolean>(false);
  const [validityOptions, setValidityOptions] = useState<IGuestValidityOption[]>([]);

  const [createGuestLimitations, setCreateGuestLimitations] = useState<ICreateGuestLimitations | null>(null);

  const defaultConfiguration = configurationStore.configuration?.default;

  const onFinish = async (values: any) => {
    let days = [];
    if (values.validity === GuestValidity.Permanent) {
      days = values.daysOfWeek[0] === 'SelectAll' ? daysOfWeek : values.daysOfWeek;
    }

    const getDatePeriod = (startDate: any, endDate: any) => {
      return {
        startDate: isShowDateRange ? startDate.format(DATE_YYYY_MM_DD) : null,
        endDate: isShowDateRange ? endDate.format(DATE_YYYY_MM_DD) : null,
      };
    };

    const guest: ICreateGuest = {
      firstName: values.firstName,
      lastName: values.lastName,
      type: values.type,
      validity: mapToServerGuestValidity(values.validity),
      isKeyPermissionAllowed: values.isKeyPermissionAllowed,
      daysOfWeek: isShowDaysOfVisits ? days : [],
      period: getDatePeriod(values.startDate, values.endDate),
      restrictions: values.restrictions,
      companyName: isShowCompanyName ? values.companyName : '',
      visitDate: getVisitDateByGuestValidity(values.validity, values.visitDate),
      carNumber: values.carNumber,
      carState: values.carState,
      phone: values.phone,
      countryCode: values.countryCode,
    };
    if (isEdit) {
      const guestToUpdate = guest as IUpdateGuest;
      guestToUpdate.id = initData!.id!;

      const result = await guestService.handleGuestsLimitationsErrors(
        () => guestService.updateGuest(initData!.residentId, initData!.addressId, guestToUpdate),
        resetLimitations,
      );
      hideModal(true, result);
    } else {
      const result = await guestService.handleGuestsLimitationsErrors(
        () => guestService.createGuest(initData!.residentId, initData!.addressId, guest),
        resetLimitations,
      );
      hideModal(true, result);
    }
  };

  // init guest limitations
  useEffect(() => {
    if (!initData) {
      return;
    }

    residentsService.getResidentsAddresseDetails(initData.residentId, initData.addressId).then((res) => {
      const limitations = guestService.getLimitations(res.ownershipType, true);
      handleValitidtyOption(limitations?.isPermanentAllowed ?? null);

      setCreateGuestLimitations(limitations);
    });
  }, [isOpen]);

  const handleValitidtyOption = (isPermanentAllowed: boolean | null) => {
    // handel default validity
    if (initData && !initData.validity && defaultConfiguration?.guestValidity) {
      if (defaultConfiguration.guestValidity === GuestValidity.Permanent && isPermanentAllowed === false) {
        validityChangeHandle(GuestValidity.OneDay);
        form.setFieldValue('validity', GuestValidity.OneDay);
      } else if (defaultConfiguration.guestValidity) {
        validityChangeHandle(defaultConfiguration.guestValidity.toString());
        form.setFieldValue('validity', defaultConfiguration.guestValidity);
      }
    }

    if (isPermanentAllowed !== false) {
      let options = guestService.getDefaultValidityOptions();
      setValidityOptions(options);
      return;
    }

    if (!isPermanentAllowed) {
      let options = guestService.getLimitedValidityOptions();
      setValidityOptions(options);
    }
  };

  const resetDateRange = () => {
    form.setFieldValue('startDate', null);
    form.setFieldValue('endDate', null);
  };

  const resetVisitDate = () => {
    form.setFieldValue('visitDate', null);
  };

  const resetLimitations = () => {
    if (!initData) return;

    residentsService.getResidentsAddresseDetails(initData.residentId, initData.addressId).then((res) => {
      const limitations = guestService.getLimitations(res.ownershipType, true);
      handleValitidtyOption(limitations?.isPermanentAllowed ?? null);
      resetVisitDate();
      resetDateRange();

      setCreateGuestLimitations(limitations);
    });
  };

  const hideModal = (...rest: any) => {
    form.resetFields();
    notification.destroy();
    if (onClose) {
      onClose(...rest);
    }
  };

  useEffect(() => {
    if (form && isOpen) {
      form.setFieldsValue(initData);
      if (initData?.validity) {
        validityChangeHandle(initData.validity);
      }
      if (initData?.type) {
        typeChangeHandle(initData.type);
      }
    }
  }, [isOpen, form, initData]);

  const validityChangeHandle = (validity: string) => {
    if (validity === GuestValidity.DateRange) {
      setIsShowDateRange(true);
    } else {
      setIsShowDateRange(false);
    }
    if (validity === GuestValidity.Permanent) {
      const guestDaysOfWeek = initData?.daysOfWeek;
      if (!guestDaysOfWeek || guestDaysOfWeek.length === 0) {
        form.setFieldsValue({ daysOfWeek: daysOfWeek });
      }
      setIsShowDaysOfVisits(true);
    } else {
      setIsShowDaysOfVisits(false);
    }
    if (validity === GuestValidity.OneDay || validity === GuestValidity.OneTime) {
      setIsShowVisitDate(true);
    } else {
      setIsShowVisitDate(false);
    }
  };

  const typeChangeHandle = (guestType: GuestType) => {
    if (isServiceGuest(guestType)) {
      setIsShowCompanyName(true);
    } else {
      setIsShowCompanyName(false);
    }

    if (guestType === GuestType.UrgentService && !form.getFieldValue('validity')) {
      form.setFieldValue('validity', GuestValidity.Today);
    }
  };

  const getGuestTypes = useCallback(() => {
    if (residentStore.selectedResidentAddress?.ownershipType === ResidentTypesEnum.Developer) {
      return serviceGuestTypeOptions.filter((option) => option.value !== GuestType.UrgentService);
    } else {
      return guestTypeOptions;
    }
  }, []);

  const handleIsEndDateEnabled = (dateToCheck: dayjs.Dayjs): boolean => {
    if (createGuestLimitations === null) {
      return false;
    }

    const startDate = form.getFieldValue('startDate');
    const today = dayjs().utc().startOf('day');

    // - 1 to exclude current date
    const maxValidDate = today
      .add(createGuestLimitations.maximumStartDateAheadLimitation, 'days')
      .add(createGuestLimitations.maximumGuestValidityLimitation - 1, 'days')
      .utc()
      .startOf('day');

    return (
      !dateToCheck.isSameOrBefore(maxValidDate, 'day') ||
      !checkDateInRange(TimeDirection.After, startDate, dateToCheck, createGuestLimitations.maximumGuestValidityLimitation)
    );
  };

  const handleIsStartDateEnabled = (dateToCheck: dayjs.Dayjs): boolean => {
    if (createGuestLimitations === null) {
      return false;
    }

    const endDate = form.getFieldValue('endDate');
    const today = dayjs().utc().startOf('day');

    const maxValidDate = today.add(createGuestLimitations.maximumStartDateAheadLimitation, 'days').utc().startOf('day');

    return (
      !dateToCheck.isSameOrBefore(maxValidDate, 'day') ||
      !checkDateInRange(TimeDirection.Before, endDate, dateToCheck, createGuestLimitations.maximumGuestValidityLimitation)
    );
  };

  const handleIsVisitDateEnabled = (dateToCheck: dayjs.Dayjs): boolean => {
    if (createGuestLimitations === null) {
      return false;
    }
    const today = dayjs().utc().startOf('day');
    const maxValidDate = today.add(createGuestLimitations.maximumStartDateAheadLimitation, 'days').utc().startOf('day');

    return !dateToCheck.isSameOrBefore(maxValidDate, 'day');
  };

  return (
    <Modal centered title={title} width={550} open={isOpen} wrapClassName="custom-modal" closable={false}>
      <FormWrapper form={form} onFinish={onFinish} initialValues={initData} layout="vertical">
        <Space className="full-width" size={13}>
          <Form.Item name="type" label="Type" rules={addEditGuestValidation.type}>
            <Select options={getGuestTypes()} onChange={typeChangeHandle} placeholder="Select a type" />
          </Form.Item>
          <Form.Item name="validity" label="Validity" rules={addEditGuestValidation.validity}>
            <Select options={validityOptions} onChange={validityChangeHandle} placeholder="Select a validity" />
          </Form.Item>
        </Space>
        {isShowDateRange && (
          <Space className="full-width" size={13}>
            <Form.Item name="startDate" label="From" rules={addEditGuestValidation.startDate}>
              <EiqDatePicker placeholder="Start date" format={DATE_MMMM_D_YYYY} disabledDate={handleIsStartDateEnabled} />
            </Form.Item>
            <Form.Item name="endDate" label="To" rules={addEditGuestValidation.endDate} dependencies={['startDate']}>
              <EiqDatePicker placeholder="End date" format={DATE_MMMM_D_YYYY} disabledDate={handleIsEndDateEnabled} />
            </Form.Item>
          </Space>
        )}
        {(isShowVisitDate || isShowDaysOfVisits) && (
          <Space className="full-width" size={13}>
            {isShowVisitDate && (
              <Form.Item name="visitDate" label="Visit date" rules={addEditGuestValidation.visitDate}>
                <EiqDatePicker placeholder="Visit date" format={DATE_MMMM_D_YYYY} disabledDate={handleIsVisitDateEnabled} />
              </Form.Item>
            )}
            {isShowDaysOfVisits && (
              <Form.Item name="daysOfWeek" label="Days of week for visits" rules={addEditGuestValidation.daysOfWeek}>
                <TreeSelect
                  popupClassName="eiq-select-days"
                  treeDefaultExpandAll={true}
                  treeCheckable={true}
                  treeData={daysOfWeekOptions}
                  showCheckedStrategy={SHOW_PARENT}
                  maxTagCount={1}
                  showSearch={false}
                  placeholder="Select a days of week for visits"
                />
              </Form.Item>
            )}
          </Space>
        )}
        {isShowCompanyName && (
          <Space className="full-width" size={13}>
            <Form.Item name="companyName" label="Company name" rules={addEditGuestValidation.companyName}>
              <Input name="companyName" placeholder="Enter company name" />
            </Form.Item>
          </Space>
        )}
        <Space className="full-width" size={13}>
          <Form.Item name="firstName" label="First name" rules={!isShowCompanyName ? addEditGuestValidation.firstName : undefined}>
            <Input name="firstName" placeholder="Enter first name" />
          </Form.Item>
          <Form.Item name="lastName" label="Last name" rules={!isShowCompanyName ? addEditGuestValidation.lastName : undefined}>
            <Input name="lastName" placeholder="Enter last name" />
          </Form.Item>
        </Space>
        <Space className="full-width" size={13}>
          <Form.Item name="carNumber" label="License Plate">
            <Input name="carNumber" placeholder="Enter license plate" />
          </Form.Item>
          <Form.Item name="carState" label="License state">
            <SelectState placeholder="Select a license state" allowClear={true} />
          </Form.Item>
        </Space>
        <Space className="full-width" size={13}>
          <Form.Item name="isKeyPermissionAllowed" label="Key permission">
            <Select options={keyPermissionOptions} placeholder="Select key permission" />
          </Form.Item>
          <InputPhoneFormItem label="Phone" numberFieldName={'phone'} countryCodeFieldName={'countryCode'} />
        </Space>

        <Space className="full-width" size={13}>
          <Form.Item name="restrictions" label="Restrictions">
            <TextArea name="restrictions" placeholder="Enter restrictions" />
          </Form.Item>
        </Space>
        <Space className="footer">
          <Button type="link" onClick={() => hideModal(false)} className="eiq-button">
            Cancel
          </Button>
          <Button type="primary" htmlType="submit" icon={<SaveOutlined />} className="eiq-button">
            Save
          </Button>
        </Space>
      </FormWrapper>
    </Modal>
  );
};

export default React.memo(AddEditGuestModal, (prevProps, nextProps) => {
  return prevProps.isOpen === nextProps.isOpen;
});
